import React, { useRef, useEffect, useCallback, useMemo, Suspense, useImperativeHandle, forwardRef } from 'react';
import { LoadPanel } from 'devextreme-react/load-panel';
import Swal from 'sweetalert2';
import { DateUtils } from 'react-day-picker';
import { CardTypes } from '../../helpers/fixcodes.js';
import { DuplicateReservationComponent } from '../Shared/DuplicateReservationComponent';
import './reservation-engine.css';

// Sub-components (eagerly loaded)
import CustomerDetailsCard from './CharterTransfer/CustomerDetailsCard';
import CharterDetailsCard from './CharterTransfer/CharterDetailsCard';
import DateTimeCard from './CharterTransfer/DateTimeCard';
import PickupDropoffCard from './CharterTransfer/PickupDropoffCard';
import OtherDetailsCard from './CharterTransfer/OtherDetailsCard';

// Hooks
import { useCharterState } from './CharterTransfer/hooks/useCharterState';
import { useCharterApi } from './CharterTransfer/hooks/useCharterApi';
import { useCharterValidation } from './CharterTransfer/hooks/useCharterValidation';

// Lazy-loaded (must be after all imports)
const FareDetailsCard = React.lazy(() => import('./CharterTransfer/FareDetailsCard'));

const position = { of: '#bookingdiv' };
const google = window.google;

export const CharterTransferComponent = forwardRef(function CharterTransferComponent(props, ref) {
  const { customer, tripDetails, rateLookup } = props;

  // Refs
  const vehicleDriverRef = useRef(null);
  const duplicationModalRef = useRef(null);
  const vehicleTypeSelectValidatorRef = useRef(null);
  const rideTypeIdRef = useRef(0);
  const isLoadingTimeRef = useRef(false);
  const autocompletePickupRef = useRef(null);
  const autocompleteDropoffRef = useRef(null);

  // State
  const { state, setState } = useCharterState(customer, tripDetails, rateLookup);

  // API
  const api = useCharterApi(state, setState, rideTypeIdRef, isLoadingTimeRef, vehicleDriverRef, duplicationModalRef, props);

  // Validation
  const { validate } = useCharterValidation(state, setState, vehicleTypeSelectValidatorRef);

  // cardExpiry formatter (kept in parent since it references currentTime)
  const cardExpiry = useCallback((val) => {
    const currentTime = sessionStorage.getItem('session_locationTime') === ''
      ? new Date()
      : new Date(sessionStorage.getItem('session_locationTime'));
    const limitMonthCreditCard = (v, max) => {
      if (v.length === 1 && v[0] > max[0]) v = '0' + v;
      if (v.length === 2) { if (Number(v) === 0) { v = '01'; } else if (v > max) { v = max; } }
      return v;
    };
    const limitYearCreditCard = (v, min) => {
      if (v.length === 1 && v[0] < min[0]) v = '0' + v;
      if (v.length === 2) { if (Number(v) === 0) { v = '01'; } else if (v < min) { v = min; } }
      return v;
    };
    const month = limitMonthCreditCard(val.substring(0, 2), '12');
    const date = limitYearCreditCard(val.substring(2, 4), currentTime.getFullYear().toString().substring(2, 4));
    return month + (date.length ? '/' + date : '');
  }, []);

  // Mount: load page data and setup Google autocomplete
  useEffect(() => {
    let isMounted = true;
    setState({ loadPanelVisible: true });

    const init = async () => {
      if (state.customer != null) {
        await api.loadPage(state.customer);
      }

      // Google autocomplete setup (after DOM ready)
      const options = { types: ['establishment'] };
      const puEl = document.getElementById('pickupGoogle');
      const doEl = document.getElementById('dropoffGoogle');
      if (puEl && google && google.maps) {
        autocompletePickupRef.current = new google.maps.places.Autocomplete(puEl, options);
        autocompletePickupRef.current.addListener('place_changed', handlePickupPlaceSelect);
      }
      if (doEl && google && google.maps) {
        autocompleteDropoffRef.current = new google.maps.places.Autocomplete(doEl, options);
        autocompleteDropoffRef.current.addListener('place_changed', handleDropoffPlaceSelect);
      }

      if (isMounted) setState({ loadPanelVisible: false });
    };

    init();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Input handlers ----

  const handleInputChange = useCallback((event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (name === 'recurringTypeId') {
      setState({ selectedDays: [], isWeekSun: false, isWeekMon: false, isWeekTue: false, isWeekWed: false, isWeekThu: false, isWeekFri: false, isWeekSat: false, isMonthSun: false, isMonthMon: false, isMonthTue: false, isMonthWed: false, isMonthThu: false, isMonthFri: false, isMonthSat: false, [name]: value });
      setState((prev) => ({ errors: { ...prev.errors, recurring: '' } }));
    } else if (name === 'serviceSourceId') {
      if (value === '1') setState({ otherAffiliateId: 0 });
      setState({ [name]: value });
      setState((prev) => ({ errors: { ...prev.errors, [name]: '' } }));
    } else if (name === 'paymentTermId') {
      if (value === '2') setState({ tripTypeId: '1', noOfTrip: 1 });
      setState({ [name]: value });
      setState((prev) => ({ errors: { ...prev.errors, [name]: '' } }));
    } else if (name === 'creditCard') {
      CardTypes.forEach((item) => {
        if (event.target.value.trim().match(item.pattern)) setState({ cardType: item.name, cardImage: item.image });
      });
      setState({ creditCard: event.target.value });
      if (event.target.value === '') setState({ cardType: '', cardImage: '' });
      setState((prev) => ({ errors: { ...prev.errors, cardType: '' } }));
    } else if (name === 'checkNumber') {
      const re = /^[0-9\b]+$/;
      if (event.target.value === '' || re.test(event.target.value)) {
        setState({ checkNumber: event.target.value });
        setState((prev) => ({ errors: { ...prev.errors, checkNumber: '' } }));
      }
    } else {
      setState({ [name]: value });
      setState((prev) => ({ errors: { ...prev.errors, [name]: '' } }));
    }
  }, [setState]);

  const handlePickupZoneChange = useCallback((selectedOption) => {
    const zoneId = selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0;
    setState({ pickupZoneId: zoneId });
    setState((prev) => ({ errors: { ...prev.errors, pickupZoneId: '' } }));
    if (selectedOption.selectedItem != null) {
      api.getRideTypeId(zoneId, state.dropoffZoneId);
      api.getCustomerRateTaxSetting(zoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, parseInt(state.noOfPax | 0), 0, state.isMeetGreet);
    }
  }, [state, setState, api]);

  const handleDropoffZoneChange = useCallback((selectedOption) => {
    const zoneId = selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0;
    setState({ dropoffZoneId: zoneId });
    setState((prev) => ({ errors: { ...prev.errors, dropoffZoneId: '' } }));
    if (selectedOption.selectedItem != null) {
      api.getRideTypeId(state.pickupZoneId, zoneId);
      api.getCustomerRateTaxSetting(state.pickupZoneId, zoneId, state.puDateTime, state.vehicleTypeId, parseInt(state.noOfPax | 0), 0, state.isMeetGreet);
    }
  }, [state, setState, api]);

  const handleAirlineChange = useCallback((selectedOption) => {
    setState({ airlineId: selectedOption.selectedItem != null ? selectedOption.selectedItem.airLineId : 0 });
  }, [setState]);

  const handleAirlineReturnChange = useCallback((selectedOption) => {
    setState({ airlineIdReturn: selectedOption.selectedItem != null ? selectedOption.selectedItem.airLineId : 0 });
    setState((prev) => ({ errors: { ...prev.errors, airlineIdReturn: '' } }));
  }, [setState]);

  const handleVehicleTypeChange = useCallback((selectedOption) => {
    try {
      const vTypeId = Number(selectedOption.selectedItem !== null ? selectedOption.selectedItem.rowIndex : 0);
      setState({ vehicleTypeId: vTypeId });
      setState((prev) => ({ errors: { ...prev.errors, vehicleTypeId: '' } }));
      if (selectedOption !== null) {
        api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, vTypeId, parseInt(state.noOfPax | 0), 0, state.isMeetGreet);
      }
    } catch (ex) { console.log(ex); }
  }, [state, setState, api]);

  const handleAffiliateChange = useCallback((selectedOption) => {
    setState({ otherAffiliateId: selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0 });
    setState((prev) => ({ errors: { ...prev.errors, otherAffiliateId: '' } }));
  }, [setState]);

  const handleReservationModeChange = useCallback((selectedOption) => {
    setState({ reservationModeId: selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0 });
    setState((prev) => ({ errors: { ...prev.errors, reservationModeId: '' } }));
  }, [setState]);

  const handleDayClick = useCallback((day, { selected }) => {
    const selectedDays = [...state.selectedDays];
    if (selected) {
      const selectedIndex = selectedDays.findIndex((d) => DateUtils.isSameDay(d, day));
      selectedDays.splice(selectedIndex, 1);
    } else {
      selectedDays.push(day);
    }
    setState({ selectedDays });
    setState((prev) => ({ errors: { ...prev.errors, recurring: '' } }));
  }, [state.selectedDays, setState]);

  const handlePaxCountChange = useCallback((value) => {
    setState({ noOfPax: value });
    api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, value, 0, state.isMeetGreet);
    if (value > 0) setState((prev) => ({ errors: { ...prev.errors, noOfPax: '' } }));
  }, [state, setState, api]);

  const handleTripCountChange = useCallback((value) => {
    setState({ noOfTrip: value });
    setState((prev) => ({ errors: { ...prev.errors, noOfTrip: '' } }));
  }, [setState]);

  const handlePaxCountReturnChange = useCallback((value) => {
    setState({ noOfPaxReturn: value });
  }, [setState]);

  const handleTripTypeChange = useCallback((event) => {
    const value = event.target.value;
    const currentTime = sessionStorage.getItem('session_locationTime') === '' ? new Date() : new Date(sessionStorage.getItem('session_locationTime'));
    const currentHour = new Date(currentTime).getHours();
    const currentMinute = new Date(currentTime).getMinutes();
    setState({
      tripTypeId: value,
      recurringTypeId: 2, selectedDays: [], recurringEndDate: '',
      isWeekSun: false, isWeekMon: false, isWeekTue: false, isWeekWed: false, isWeekThu: false, isWeekFri: false, isWeekSat: false,
      isMonthSun: false, isMonthMon: false, isMonthTue: false, isMonthWed: false, isMonthThu: false, isMonthFri: false, isMonthSat: false,
      flightNumberReturn: '', terminalTypeReturn: 0, terminalReturn: '', noOfPaxReturn: 1, noOfTrip: 1,
      flightHourReturn: currentHour, flightMinuteReturn: currentMinute,
    });
    setState((prev) => ({
      errors: {
        ...prev.errors, recurring: '', airlineIdReturn: '', flightNumberReturn: '', noOfPaxReturn: '', flightTimeReturn: '', puDateTimeReturn: '',
      },
    }));
  }, [setState]);

  const handleMeetGreetChange = useCallback((event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setState({ isMeetGreet: value, meetGreet: 0 });
    if (!value) api.calculateFare('meetGreet', 0);
    const paxCount = parseInt(state.noOfPax | 0);
    api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, value);
  }, [state, setState, api]);

  const handleMgToPay = useCallback((e) => {
    setState({ mgToPay: e.target.value, payMeetGreetId: 0, payMeetGreetAmount: 0.0 });
  }, [setState]);

  const handleDriverCommissionPay = useCallback((e) => {
    setState({ mgDriverComms: e.target.value });
  }, [setState]);

  const handleGreeterChange = useCallback((selectedOption) => {
    setState({ payMeetGreetId: selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0 });
    setState((prev) => ({ errors: { ...prev.errors, payMeetGreetId: '' } }));
  }, [setState]);

  const handleAgentCommissionChange = useCallback((selectedOption) => {
    setState({ agentCommissionId: selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0 });
  }, [setState]);

  const handlePaymentTypeChange = useCallback((selectedOption) => {
    const paytypeId = selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0;
    setState({
      paymentTypeId: paytypeId,
      checkNumber: '', checkDate: '', checkBank: '', existingCardId: 0, creditCard: '', cardType: '', cardExpiry: '', voucherCompanyId: 0, voucherCode: '', chkPaymentGatewaySelection: '1',
    });
    setState((prev) => ({ errors: { ...prev.errors, checkNumber: '', checkBank: '', checkDate: '', existingCardId: '', creditCard: '', cardType: '', cardExpiry: '', voucherCompanyId: '', voucherCode: '' } }));

    if (parseInt(state.chkPaymentGatewaySelection) === 2) {
      if (state.rideId > 0) {
        if (paytypeId === 2 && state.creditCard.includes('x')) { /* no change */ }
        else if ((paytypeId === 2 && !state.creditCard.includes('x')) || paytypeId === 3) { setState({ paidAmount: parseFloat(state.totalFare).toFixed(3), paymentTermId: 1 }); }
        else { setState({ paidAmount: parseFloat(0).toFixed(3) }); }
      } else {
        if (paytypeId === 2 || paytypeId === 3) { setState({ paidAmount: parseFloat(state.totalFare).toFixed(3), paymentTermId: 1 }); }
        else { setState({ paidAmount: parseFloat(0).toFixed(3) }); }
      }
    } else {
      if (state.rideId > 0) {
        if (paytypeId === 2 && state.creditCard.includes('x')) { /* no change */ }
        else if (paytypeId === 2 && !state.creditCard.includes('x')) { setState({ paidAmount: parseFloat(state.totalFare).toFixed(3), paymentTermId: 1 }); }
        else { setState({ paidAmount: parseFloat(0).toFixed(3) }); }
      } else {
        if (paytypeId === 2) { setState({ paidAmount: parseFloat(state.totalFare).toFixed(3), paymentTermId: 1 }); }
        else { setState({ paidAmount: parseFloat(0).toFixed(3) }); }
      }
    }
  }, [state, setState]);

  const handleVoucherCompanyChange = useCallback((selectedOption) => {
    setState({ voucherCompanyId: selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0 });
    setState((prev) => ({ errors: { ...prev.errors, voucherCompanyId: '' } }));
  }, [setState]);

  const handleExistingCardChange = useCallback((selectedOption) => {
    setState({ existingCardId: selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0 });
    setState((prev) => ({ errors: { ...prev.errors, existingCardId: '' } }));
  }, [setState]);

  const handleRecurringEndDateChange = useCallback((date) => {
    setState({ recurringEndDate: date.value });
    setState((prev) => ({ errors: { ...prev.errors, recurringEndDate: '' } }));
  }, [setState]);

  const handleFlightHourChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightHour: e.selectedItem.id });
  }, [setState]);

  const handleFlightMinuteChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightMinute: e.selectedItem.id });
  }, [setState]);

  const handleFlightHourReturnChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightHourReturn: e.selectedItem.id });
  }, [setState]);

  const handleFlightMinuteReturnChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightMinuteReturn: e.selectedItem.id });
  }, [setState]);

  const handlePickupDateTimeChange = useCallback((date) => {
    setState({ puDateTime: date.value });
    setState((prev) => ({ errors: { ...prev.errors, puDateTime: '' } }));
    if (date != null) {
      api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, date, state.vehicleTypeId, parseInt(state.noOfPax | 0), 0, state.isMeetGreet);
    }
  }, [state, setState, api]);

  const handlePickupDateTimeReturnChange = useCallback((date) => {
    setState({ puDateTimeReturn: date.value });
    setState((prev) => ({ errors: { ...prev.errors, puDateTimeReturn: '' } }));
  }, [setState]);

  const handleRateChange = useCallback(async (ctrl, value) => {
    if (isLoadingTimeRef.current) return;
    if (value === undefined) value = 0;
    setState({ [ctrl]: value });
    await api.calculateFare(ctrl, value);
  }, [setState, api, isLoadingTimeRef]);

  const handleFareViewChange = useCallback((view) => {
    setState({ fareView: view });
  }, [setState]);

  const handlePaymentGatewaySelectionChange = useCallback((event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setState({
      chkPaymentGatewaySelection: value,
      checkNumber: '', checkDate: '', checkBank: '', existingCardId: 0, creditCard: '', cardType: '', cardExpiry: '', voucherCompanyId: 0, voucherCode: '',
    });
    setState((prev) => ({ errors: { ...prev.errors, checkNumber: '', checkBank: '', checkDate: '', existingCardId: '', creditCard: '', cardType: '', cardExpiry: '', voucherCompanyId: '', voucherCode: '' } }));
  }, [setState]);

  // ---- Autosuggest pickup handlers ----
  const onChangePickupLocation = useCallback((event, { newValue }) => {
    setState({ pickupLocation: newValue });
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
  }, [setState]);

  const onBlurPickupLocation = useCallback(() => {
    const index2 = state.bookingAddress.length > 0 ? state.bookingAddress.map((item) => item.name).indexOf(state.pickupLocation) : -1;
    if (index2 === -1) {
      setState({ pickupLocation: '', pickupAddress: '', pickupAddressType: '', pickupCity: '', pickupState: '', pickupZipCode: '', pickupZoneId: 0, pickupLatitude: 0, pickupLongitude: 0 });
      api.getRideTypeId(0, state.dropoffZoneId);
    }
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
  }, [state, setState, api]);

  const getPickupSuggestionValue = useCallback((suggestion) => {
    if (suggestion.isNotFound) return '';
    setState({
      pickupLocation: suggestion.name, pickupAddress: suggestion.address, pickupAddressType: suggestion.addressType,
      pickupCity: suggestion.city, pickupState: suggestion.state, pickupZipCode: suggestion.zipCode,
      pickupZoneId: suggestion.zoneId, pickupLatitude: suggestion.latitude, pickupLongitude: suggestion.longitude,
    });
    if (state.dropdownZone.findIndex((opt) => opt.id === suggestion.zoneId) < 0) {
      setState({ pickupZoneId: 0 });
    } else {
      api.getRideTypeId(suggestion.zoneId, state.dropoffZoneId);
      api.getCustomerRateTaxSetting(suggestion.id, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, parseInt(state.noOfPax | 0), 0, state.isMeetGreet);
      setState((prev) => ({ errors: { ...prev.errors, pickupZoneId: '' } }));
    }
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
    return suggestion.name;
  }, [state, setState, api]);

  const onPickupSuggestionsFetchRequested = useCallback(({ value }) => {
    api.getBookingSuggestions(value).then((data) => setState({ pickupSuggestions: data }));
  }, [api, setState]);

  const onPickupSuggestionsClearRequested = useCallback(() => {
    setState({ pickupSuggestions: [] });
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
  }, [setState]);

  // ---- Autosuggest dropoff handlers ----
  const onChangeDropoffLocation = useCallback((event, { newValue }) => {
    setState({ dropoffLocation: newValue });
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
  }, [setState]);

  const onBlurDropoffLocation = useCallback(() => {
    const index2 = state.bookingAddress.length > 0 ? state.bookingAddress.map((item) => item.name).indexOf(state.dropoffLocation) : -1;
    if (index2 === -1) {
      setState({ dropoffLocation: '', dropoffAddress: '', dropoffAddressType: '', dropoffCity: '', dropoffState: '', dropoffZipCode: '', dropoffZoneId: 0, dropoffLatitude: 0, dropoffLongitude: 0 });
      api.getRideTypeId(state.pickupZoneId, 0);
    }
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
  }, [state, setState, api]);

  const getDropoffSuggestionValue = useCallback((suggestion) => {
    if (suggestion.isNotFound) return '';
    setState({
      dropoffLocation: suggestion.name, dropoffAddress: suggestion.address, dropoffAddressType: suggestion.addressType,
      dropoffCity: suggestion.city, dropoffState: suggestion.state, dropoffZipCode: suggestion.zipCode,
      dropoffZoneId: suggestion.zoneId, dropoffLatitude: suggestion.latitude, dropoffLongitude: suggestion.longitude,
    });
    if (state.dropdownZone.findIndex((opt) => opt.id === suggestion.zoneId) < 0) {
      setState({ dropoffZoneId: 0 });
    } else {
      api.getRideTypeId(state.pickupZoneId, suggestion.zoneId);
      api.getCustomerRateTaxSetting(state.pickupZoneId, suggestion.zoneId, state.puDateTime, state.vehicleTypeId, parseInt(state.noOfPax | 0), 0, state.isMeetGreet);
      setState((prev) => ({ errors: { ...prev.errors, dropoffZoneId: '' } }));
    }
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
    return suggestion.name;
  }, [state, setState, api]);

  const onDropoffSuggestionsFetchRequested = useCallback(({ value }) => {
    api.getBookingSuggestions(value).then((data) => setState({ dropoffSuggestions: data }));
  }, [api, setState]);

  const onDropoffSuggestionsClearRequested = useCallback(() => {
    setState({ dropoffSuggestions: [] });
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
  }, [setState]);

  // ---- Toggle handlers ----
  const handlePickupToggleChange = useCallback((event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setState({ pickupLocation: '', pickupAddress: '', pickupAddressType: '', pickupCity: '', pickupState: '', pickupZipCode: '', pickupLatitude: 0, pickupLongitude: 0, isPickupGoogleBooking: value });
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
    const el = document.getElementById('pickupGoogle');
    if (el) el.value = '';
  }, [setState]);

  const handleDropoffToggleChange = useCallback((event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setState({ dropoffLocation: '', dropoffAddress: '', dropoffAddressType: '', dropoffCity: '', dropoffState: '', dropoffZipCode: '', dropoffLatitude: 0, dropoffLongitude: 0, isDropoffGoogleBooking: value });
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
    const el = document.getElementById('dropoffGoogle');
    if (el) el.value = '';
  }, [setState]);

  // ---- Google place select ----
  const handlePlaceSearch = useCallback((autocompletePlace) => {
    const place = autocompletePlace.getPlace();
    const placeObject = {};
    if (place != null && place.formatted_address != null && place.formatted_address.trim() !== '') {
      placeObject.location = place.name;
      placeObject.latitude = place.geometry.location.lat();
      placeObject.longitude = place.geometry.location.lng();
      let splitAddress = [];
      place.formatted_address = place.formatted_address.replace(/-/g, ',');
      splitAddress = place.formatted_address.split(',');
      if (splitAddress.length === 1) { placeObject.address1 = splitAddress[0]; placeObject.cityName = ''; placeObject.zipCode = ''; placeObject.stateName = ''; placeObject.countryName = ''; }
      else if (splitAddress.length === 2) { placeObject.address1 = splitAddress[0]; placeObject.cityName = splitAddress[0]; placeObject.stateName = splitAddress[1].trim().search(' ') > 0 ? splitAddress[1].trim().split(' ')[0] : splitAddress[1].trim(); placeObject.zipCode = splitAddress[1].trim().search(' ') > 0 ? splitAddress[1].trim().split(' ')[1] : ' '; placeObject.countryName = ''; }
      else if (splitAddress.length === 3) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim(); placeObject.cityName = splitAddress[1].trim(); placeObject.stateName = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[0] : splitAddress[2].trim(); placeObject.zipCode = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[1] : ' '; placeObject.countryName = ''; }
      else if (splitAddress.length === 4) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim(); placeObject.cityName = splitAddress[1].trim(); placeObject.stateName = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[0] : splitAddress[2].trim(); placeObject.zipCode = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[3].trim(); }
      else if (splitAddress.length === 5) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim() + ' ' + splitAddress[2].trim(); placeObject.cityName = splitAddress[2].trim(); placeObject.stateName = splitAddress[3].trim().search(' ') > 0 ? splitAddress[3].trim().split(' ')[0] : splitAddress[3].trim(); placeObject.zipCode = splitAddress[3].trim().search(' ') > 0 ? splitAddress[3].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[4].trim(); }
      else if (splitAddress.length === 6) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim() + ' ' + splitAddress[2].trim() + ' ' + splitAddress[3].trim(); placeObject.cityName = splitAddress[3].trim(); placeObject.stateName = splitAddress[4].trim().search(' ') > 0 ? splitAddress[4].trim().split(' ')[0] : splitAddress[4].trim(); placeObject.zipCode = splitAddress[4].trim().search(' ') > 0 ? splitAddress[4].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[5].trim(); }
      else { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim() + ' ' + splitAddress[2].trim() + ' ' + splitAddress[3].trim() + ' ' + splitAddress[4].trim(); placeObject.cityName = splitAddress[4].trim(); placeObject.stateName = splitAddress[5].trim().search(' ') > 0 ? splitAddress[5].trim().split(' ')[0] : splitAddress[5].trim(); placeObject.zipCode = splitAddress[5].trim().search(' ') > 0 ? splitAddress[5].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[6].trim(); }
    }
    return placeObject;
  }, []);

  const handlePickupPlaceSelect = useCallback(() => {
    const placeObj = handlePlaceSearch(autocompletePickupRef.current);
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
    if (placeObj && placeObj.location) {
      setState({ pickupLocation: placeObj.location, pickupAddress: placeObj.address1, pickupAddressType: placeObj.addressType, pickupCity: placeObj.cityName, pickupState: placeObj.stateName, pickupZipCode: placeObj.zipCode, pickupLatitude: placeObj.latitude, pickupLongitude: placeObj.longitude });
    } else {
      setState({ pickupLocation: '', pickupAddress: '', pickupAddressType: '', pickupCity: '', pickupState: '', pickupZipCode: '', pickupLatitude: 0, pickupLongitude: 0 });
    }
  }, [handlePlaceSearch, setState]);

  const handleDropoffPlaceSelect = useCallback(() => {
    const placeObj = handlePlaceSearch(autocompleteDropoffRef.current);
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
    if (placeObj && placeObj.location) {
      setState({ dropoffLocation: placeObj.location, dropoffAddress: placeObj.address1, dropoffAddressType: placeObj.addressType, dropoffCity: placeObj.cityName, dropoffState: placeObj.stateName, dropoffZipCode: placeObj.zipCode, dropoffLatitude: placeObj.latitude, dropoffLongitude: placeObj.longitude });
    } else {
      setState({ dropoffLocation: '', dropoffAddress: '', dropoffAddressType: '', dropoffCity: '', dropoffState: '', dropoffZipCode: '', dropoffLatitude: 0, dropoffLongitude: 0 });
    }
  }, [handlePlaceSearch, setState]);

  // ---- Submit ----
  const charterTransferSubmit = useCallback(async (isAttachment, isSendEmail) => {
    setTimeout(() => setState({ isAttachment, isSendEmail }), 100);
    if (!validate()) return;

    if (state.rideId > 0) {
      if (parseInt(props.tripDetails.tripStatusId) === 116) {
        if (parseInt(props.tripDetails.invoiceNo) > 0) {
          await Swal.fire({ title: '<small>Unable to modify</small>', text: "Reservation is completed & invoiced, you can't modify/update Reservation.", icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
          return;
        } else {
          const uTypeId = parseInt(window.sessionStorage.getItem('session_userTypeId'));
          if (uTypeId !== 33 && uTypeId !== 6 && uTypeId !== 2) {
            await Swal.fire({ title: '<small>Unable to modify</small>', text: "Reservation is completed, you can't modify/update Reservation.", icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
            return;
          }
        }
      }
      if (parseInt(props.tripDetails.tripStatusId) === 111 || parseInt(props.tripDetails.tripStatusId) === 118) {
        await Swal.fire({ title: '<small>Unable to modify</small>', text: "Reservation is void/cancelled, you can't modify/update Reservation.", icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
        return;
      }
    }

    if (parseInt(state.customer.accountStatusId) === 2) {
      await Swal.fire({ title: '<small>Account on hold</small>', text: 'This account is on hold. You are not allow to book any reservation.', icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }
    if (parseInt(state.customer.accountStatusId) === 3) {
      await Swal.fire({ title: '<small>Account on close</small>', text: 'This account is on close. You are not allow to book any reservation.', icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }

    const paxCount = parseInt(state.noOfPax | 0);
    const vehicleDetails = state.dropdownVehicleType.filter((opt) => opt.rowIndex === state.vehicleTypeId);
    if (!vehicleDetails || vehicleDetails.length === 0) {
      await Swal.fire({ title: '<small>Invalid Pax Count</small>', text: 'Please select charter existing charter pax details not found!', icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }
    if (paxCount > vehicleDetails[0].apiPaxNumber) {
      await Swal.fire({ title: '<small>Invalid Pax Count</small>', text: 'Please enter pax count less than or equal to ' + vehicleDetails[0].apiPaxNumber + ' for ' + vehicleDetails[0].apiVehicleTypeDescription + '!', icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }

    if (state.customerRateTax == null) {
      await api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
    }
    if (state.customerRateTax == null) {
      await Swal.fire({ title: '<small>Oops..</small>', text: 'Customer setting not found!', icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }

    if (state.tripTypeId === '2') {
      let isConfirm = false;
      await Swal.fire({ title: '<small>Are you sure</small>', html: 'You are going book a return trip!<br> Do you want to still continue?', icon: 'question', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Continue', allowOutsideClick: false }).then((result) => { if (result.isConfirmed) isConfirm = true; });
      if (!isConfirm) return;
    }

    let isBookingConfirm = false;
    const strAlert = 'Please Let me confirm your reservation.<br><br>You have a [' + state.puDateTime.toLocaleTimeString() + '] pickup on [' + state.puDateTime.toLocaleDateString() + ']<br>From the [' + state.pickupLocation + ']<br>for a total of [' + paxCount + '] passanger(s)<br>going to [' + state.dropoffLocation + ']<br>SPECIAL INSTRUCTION: ' + state.specialInstruction + '<br>Is this Correct?';
    await Swal.fire({ title: '<small>Are you sure</small>', html: strAlert, icon: 'question', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes', allowOutsideClick: false }).then((result) => { if (result.isConfirmed) isBookingConfirm = true; });
    if (!isBookingConfirm) return;

    if (state.rideId > 0) {
      props.submitFunction(true);
      await api.bookReservation();
    } else {
      props.submitFunction(true);
      const isDuplicateFound = await api.checkDuplicateReservation(true);
      if (isDuplicateFound) {
        props.submitFunction(false);
      } else {
        await api.bookReservation();
      }
    }
  }, [state, setState, validate, api, props]);

  // Expose methods to parent class component via ref (BookingComponent uses this.refs.charterTransferRef.method())
  useImperativeHandle(ref, () => ({
    charterTransferSubmit,
    copyReservation: api.copyReservation,
    loadPage: api.loadPage,
    checkDuplicateReservation: api.checkDuplicateReservation,
    clearAllControls: () => {},
  }), [charterTransferSubmit, api.copyReservation, api.loadPage, api.checkDuplicateReservation]);

  useEffect(() => {
    if (props.onReady) props.onReady({ charterTransferSubmit, copyReservation: api.copyReservation });
  }, [charterTransferSubmit, api.copyReservation, props]);

  // inputProps for autosuggest (memoised)
  const inputPropsPickup = useMemo(() => ({
    placeholder: 'Enter pick-up location.',
    value: state.pickupLocation,
    onChange: onChangePickupLocation,
    onBlur: onBlurPickupLocation,
  }), [state.pickupLocation, onChangePickupLocation, onBlurPickupLocation]);

  const inputPropsDropoff = useMemo(() => ({
    placeholder: 'Enter drop off location.',
    value: state.dropoffLocation,
    onChange: onChangeDropoffLocation,
    onBlur: onBlurDropoffLocation,
  }), [state.dropoffLocation, onChangeDropoffLocation, onBlurDropoffLocation]);

  const customerType = customer != null ? customer.customerType : '';

  return (
    <div className="rx-rez-area" id="bookingdiv">
      {/* Row 1: expands to 3-col when Round Trip / Recurring is selected */}
      <div className={(state.tripTypeId === '2' || state.tripTypeId === '3') ? 'rx-row-3col' : 'rx-row-2col'}>
        <CustomerDetailsCard
          state={state}
          onChange={handleInputChange}
          errors={state.errors}
          customerType={customerType}
        />
        <CharterDetailsCard
          state={state}
          onChange={handleInputChange}
          onReservationModeChange={handleReservationModeChange}
          onVehicleTypeChange={handleVehicleTypeChange}
          onAirlineChange={handleAirlineChange}
          onFlightHourChange={handleFlightHourChange}
          onFlightMinuteChange={handleFlightMinuteChange}
          onPickupDateTimeChange={handlePickupDateTimeChange}
          onTripTypeChange={handleTripTypeChange}
          onPaxCountChange={handlePaxCountChange}
          onTripCountChange={handleTripCountChange}
          onMeetGreetChange={handleMeetGreetChange}
          rideTypeId={rideTypeIdRef.current}
          vehicleTypeSelectValidatorRef={vehicleTypeSelectValidatorRef}
          errors={state.errors}
        />
        {(state.tripTypeId === '2' || state.tripTypeId === '3') && (
          <DateTimeCard
            state={state}
            onChange={handleInputChange}
            onAirlineReturnChange={handleAirlineReturnChange}
            onFlightHourReturnChange={handleFlightHourReturnChange}
            onFlightMinuteReturnChange={handleFlightMinuteReturnChange}
            onPickupDateTimeReturnChange={handlePickupDateTimeReturnChange}
            onRecurringEndDateChange={handleRecurringEndDateChange}
            onDayClick={handleDayClick}
            onPaxCountReturnChange={handlePaxCountReturnChange}
            errors={state.errors}
          />
        )}
      </div>
      <div className="rx-row-2col">
        <PickupDropoffCard
          state={state}
          onChange={handleInputChange}
          onPickupSuggestionsFetch={onPickupSuggestionsFetchRequested}
          onPickupSuggestionsClear={onPickupSuggestionsClearRequested}
          getPickupSuggestionValue={getPickupSuggestionValue}
          onPickupToggle={handlePickupToggleChange}
          onPickupZoneChange={handlePickupZoneChange}
          onDropoffSuggestionsFetch={onDropoffSuggestionsFetchRequested}
          onDropoffSuggestionsClear={onDropoffSuggestionsClearRequested}
          getDropoffSuggestionValue={getDropoffSuggestionValue}
          onDropoffToggle={handleDropoffToggleChange}
          onDropoffZoneChange={handleDropoffZoneChange}
          inputPropsPickup={inputPropsPickup}
          inputPropsDropoff={inputPropsDropoff}
          errors={state.errors}
        />
        <OtherDetailsCard
          state={state}
          onChange={handleInputChange}
          onAffiliateChange={handleAffiliateChange}
          vehicleDriverRef={vehicleDriverRef}
          errors={state.errors}
        />
      </div>
      <Suspense fallback={<div>Loading fare details...</div>}>
        <FareDetailsCard
          state={state}
          onChange={handleInputChange}
          onRateChange={handleRateChange}
          onPaymentTypeChange={handlePaymentTypeChange}
          onGreeterChange={handleGreeterChange}
          onAgentCommissionChange={handleAgentCommissionChange}
          onMgToPay={handleMgToPay}
          onDriverCommissionPay={handleDriverCommissionPay}
          onPaymentGatewayChange={handlePaymentGatewaySelectionChange}
          onVoucherCompanyChange={handleVoucherCompanyChange}
          onExistingCardChange={handleExistingCardChange}
          cardExpiry={cardExpiry}
          rideTypeId={rideTypeIdRef.current}
          fareView={state.fareView}
          onFareViewChange={handleFareViewChange}
          errors={state.errors}
        />
      </Suspense>
      <DuplicateReservationComponent ref={duplicationModalRef} bookReservation={api.bookReservation} />
      <LoadPanel shadingColor="rgba(0,0,0,0.4)" position={position} visible={state.loadPanelVisible} showIndicator={true} shading={true} showPane={true} closeOnOutsideClick={false} />
    </div>
  );
});

export default CharterTransferComponent;